import { Crown, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { TeamJudgingResult, TeamJudgingScore } from '@/types';

// A place is worth showing from the top 35% of the stage up.
const TOP_SHARE = 0.35;

// The radar's geometry, in the SVG's 0–100 viewBox: the centre and the radius of a
// 100 score. Labels sit a little outside it.
const CENTER = 50;
const RADIUS = 34;
const LABEL_RADIUS = 46;
const RINGS = [25, 50, 75, 100];

function angleOf(index: number, count: number): number {
  return -Math.PI / 2 + (index * 2 * Math.PI) / count;
}

function pointAt(index: number, count: number, score: number, radius = RADIUS) {
  const angle = angleOf(index, count);
  const r = (Math.max(0, Math.min(100, score)) / 100) * radius;
  return { x: CENTER + r * Math.cos(angle), y: CENTER + r * Math.sin(angle) };
}

function polygon(scores: number[]): string {
  return scores
    .map((score, index) => {
      const { x, y } = pointAt(index, scores.length, score);
      return `${x},${y}`;
    })
    .join(' ');
}

function formatScore(score: number): string {
  return score.toFixed(1);
}

// "#3 de 25 · top 12%", only for a place in the top 35%. The first place is just
// "#1 de 25": a percentage says nothing more there.
function placeLabel(score: TeamJudgingScore, rankedCount: number): string | null {
  if (rankedCount === 0 || score.rank / rankedCount > TOP_SHARE) return null;
  if (score.rank === 1) return `#1 de ${rankedCount}`;
  const top = Math.max(1, Math.ceil((score.rank / rankedCount) * 100));
  return `#${score.rank} de ${rankedCount} · top ${top}%`;
}

function PlaceBadge({
  score,
  rankedCount,
  className,
}: {
  score: TeamJudgingScore;
  rankedCount: number;
  className?: string;
}) {
  const label = placeLabel(score, rankedCount);
  if (!label) return null;
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap bg-primary px-1.5 py-0.5 font-bold text-[10px] text-background',
        className,
      )}
    >
      {label}
    </span>
  );
}

// One stage's results on a hacker's dashboard: the weighted final score, and a radar
// with the team's 0–100 score on every criterion against the stage's average (the
// ghost). Hovering a criterion gives the team's score, the average and its weight.
export function JudgingResultsCard({
  result,
  titleFont,
}: {
  result: TeamJudgingResult;
  titleFont: string | null;
}) {
  const { criteria, criterionScores, final, rankedCount } = result;
  const count = criteria.length;

  return (
    <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
      <div className="flex flex-col items-center gap-2 text-center">
        <h2
          className={cn(
            'font-bold text-primary/70 text-sm uppercase tracking-wide',
            titleFont,
          )}
        >
          Resultados · {result.stageName}
          {result.isFinal && (
            <Crown className="ml-1 inline h-4 w-4 align-[-2px]" />
          )}
        </h2>
        <p
          className={cn(
            'font-bold text-6xl text-primary tabular-nums sm:text-7xl',
            titleFont,
          )}
        >
          {formatScore(final.score)}
          <span className="font-normal text-2xl text-primary/50">/100</span>
        </p>
        <p className="text-primary/70 text-sm">
          Score final ponderado · promedio de equipos{' '}
          {formatScore(final.average)}
        </p>
        <PlaceBadge score={final} rankedCount={rankedCount} className="text-xs" />
      </div>

      {count > 0 && (
        // The side padding is room for the labels around the radar.
        <div className="mx-auto mt-8 w-full max-w-lg px-10 sm:px-14">
        <div className="relative aspect-square w-full">
          <svg
            viewBox="0 0 100 100"
            className="h-full w-full overflow-visible"
            role="img"
            aria-label="Puntaje por dimensión comparado con el promedio de equipos"
          >
            {RINGS.map((ring) => (
              <polygon
                key={ring}
                points={polygon(criteria.map(() => ring))}
                className="fill-none stroke-primary/15"
                strokeWidth={0.3}
              />
            ))}
            {criteria.map((criterion, index) => {
              const end = pointAt(index, count, 100);
              return (
                <line
                  key={criterion.id}
                  x1={CENTER}
                  y1={CENTER}
                  x2={end.x}
                  y2={end.y}
                  className="stroke-primary/15"
                  strokeWidth={0.3}
                />
              );
            })}
            {/* The stage's average: the ghost to measure against. */}
            <polygon
              points={polygon(criterionScores.map((score) => score.average))}
              className="fill-primary/5 stroke-primary/50"
              strokeWidth={0.5}
              strokeDasharray="1.5 1"
            />
            <polygon
              points={polygon(criterionScores.map((score) => score.score))}
              className="fill-primary/30 stroke-primary"
              strokeWidth={0.8}
              strokeLinejoin="round"
            />
            {criterionScores.map((score, index) => {
              const { x, y } = pointAt(index, count, score.score);
              return (
                <circle
                  key={criteria[index].id}
                  cx={x}
                  cy={y}
                  r={1.2}
                  className="fill-primary"
                />
              );
            })}
          </svg>

          {criteria.map((criterion, index) => {
            const score = criterionScores[index];
            const angle = angleOf(index, count);
            const left = CENTER + LABEL_RADIUS * Math.cos(angle);
            const top = CENTER + LABEL_RADIUS * Math.sin(angle);
            return (
              <Tooltip key={criterion.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="-translate-x-1/2 -translate-y-1/2 absolute flex max-w-[40%] cursor-help flex-col items-center text-center"
                    style={{ left: `${left}%`, top: `${top}%` }}
                  >
                    <span className="flex items-center gap-0.5 font-bold text-[11px] text-primary/80 uppercase leading-tight sm:text-xs">
                      {criterion.name}
                      <Info className="h-3 w-3 shrink-0 opacity-60" />
                    </span>
                    <span className="font-bold text-lg text-primary tabular-nums leading-tight sm:text-xl">
                      {formatScore(score.score)}
                    </span>
                    {/* On a phone the list below carries the places. */}
                    <PlaceBadge
                      score={score}
                      rankedCount={rankedCount}
                      className="hidden sm:inline-block"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs whitespace-pre-line text-xs">
                  <p>
                    Tu equipo: <strong>{formatScore(score.score)}</strong> ·
                    Promedio de equipos:{' '}
                    <strong>{formatScore(score.average)}</strong> · Pondera{' '}
                    {criterion.weight}%
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-primary/70 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 border border-primary bg-primary/30" />
          Tu equipo
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 border border-primary/50 border-dashed bg-primary/5" />
          Promedio de equipos
        </span>
      </div>

      <ul className="mt-6 flex flex-col gap-2">
        {criteria.map((criterion, index) => {
          const score = criterionScores[index];
          return (
            <li
              key={criterion.id}
              className="flex flex-wrap items-center justify-between gap-2 border-primary border-l-4 pl-3"
            >
              <span className={cn('font-bold text-primary text-sm', titleFont)}>
                {criterion.name}{' '}
                <span className="font-normal text-primary/50">
                  ({criterion.weight}%)
                </span>
              </span>
              <span className="flex items-center gap-2 text-sm">
                <PlaceBadge score={score} rankedCount={rankedCount} />
                <span className="font-bold text-primary tabular-nums">
                  {formatScore(score.score)}
                </span>
                <span className="text-primary/50 tabular-nums">
                  prom. {formatScore(score.average)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-4 text-center text-primary/50 text-xs">
        Puntajes de 0 a 100, normalizados por juez y comparables entre
        dimensiones dentro del stage.
      </p>
    </div>
  );
}
